import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Type-ahead search box. Fires GET {endpoint}?q=... after user types 3+ chars.
 * Renders selectable results. Backend returns [{id, name, ...}] shape.
 */
export default function TypeAhead({
  endpoint,
  value,
  onChange,
  placeholder = "Search…",
  labelKey = "name",
  subLabelKey = null,
  testid,
  disabled,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (q.trim().length < 3) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(endpoint, { params: { q } });
        setItems(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, endpoint]);

  return (
    <div className="relative" ref={boxRef}>
      {value ? (
        <div
          data-testid={testid}
          className="flex items-center gap-2 border border-input rounded-sm px-3 py-3 min-h-[48px] bg-white cursor-pointer"
          onClick={() => !disabled && (onChange(null), setQ(""), setOpen(true))}
        >
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{value[labelKey]}</div>
            {subLabelKey && value[subLabelKey] && (
              <div className="text-xs font-mono text-muted-foreground truncate">
                {value[subLabelKey]}
              </div>
            )}
          </div>
          {!disabled && <X className="w-4 h-4 text-muted-foreground" />}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid={testid}
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              className="field pl-9 pr-9"
            />
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          </div>
          {open && q.trim().length >= 3 && (
            <ul
              data-testid={`${testid}-results`}
              className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-border rounded-sm shadow-md animate-fade-in"
            >
              {loading && <li className="px-3 py-2 text-sm text-muted-foreground">Searching…</li>}
              {!loading && items.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">No results</li>
              )}
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`${testid}-opt-${it.id}`}
                  onClick={() => { onChange(it); setOpen(false); setQ(""); }}
                  className="px-3 py-3 hover:bg-muted cursor-pointer text-sm border-b border-border last:border-0"
                >
                  <div className="font-medium">{it[labelKey]}</div>
                  {subLabelKey && it[subLabelKey] && (
                    <div className="text-xs font-mono text-muted-foreground">{it[subLabelKey]}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {open && q.trim().length > 0 && q.trim().length < 3 && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-sm px-3 py-2 text-xs text-muted-foreground">
              Type at least 3 characters
            </div>
          )}
        </>
      )}
    </div>
  );
}
